// src/pages/customers/useCustomersPage.ts
import { useEffect, useMemo, useState } from "react";

import {
  type CustomerTableFilters,
  type CustomerRow,
} from "@/components/customers/CustomerTable";

import type {
  UpsertCustomer,
} from "@/components/customers/forms/CreateCustomerForm";

import type {
  Customer as UiCustomer,
} from "@/components/customers/forms/EditCustomerForm";

import { DEFAULT_PAGE_SIZE } from "@/constants/pagination";

import {
  useCustomers,
  useCreateCustomerMutation,
  useAttachCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  lookupCustomerUser,
} from "@/api/features/customers";

import type {
  CustomerResult,
  ListCustomersQuery,
  CreateCustomerBody,
  AttachCustomerBody,
  LookupUserResult,
  UpdateCustomerBody,
  CustomerStatusFilter
} from "@/api/features/customers/types";

/* ===========================================================================
 * Helpers
 * =========================================================================== */

/** Map API CustomerResult → UI-level customer object used by forms/table */
function mapApiToUiCustomer(c: CustomerResult): UiCustomer {
  return {
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    username: c.username,
    email: c.email,
    phone: c.phone ?? "",

    // direct status from backend
    status: c.status,

    gender: c.gender ?? undefined,
    dob: c.dob ?? undefined,
    idType: c.idType ?? undefined,
    idNumber: c.idNumber ?? undefined,

    addressLine1: c.addressLine1 ?? undefined,
    addressLine2: c.addressLine2 ?? undefined,
    country: c.country ?? undefined,
    state: c.state ?? undefined,
    city: c.city ?? undefined,
  };
}

/** Map UiCustomer → table row */
function mapUiToRow(u: UiCustomer): CustomerRow {
  return {
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
    phone: u.phone || "",
    status: u.status,
  };
}

/** Best-effort mapping from create form payload → API Create body */
function mapUpsertToCreateBody(payload: UpsertCustomer): CreateCustomerBody {
  // UpsertCustomer is a subset of CreateCustomerBody, so this is safe
  const base: any = { ...payload };
  return base as CreateCustomerBody;
}

/**
 * Map create form payload → Attach body. Identity fields (firstName/lastName/
 * username/email/phone) are dropped — they already belong to the target user.
 */
function mapUpsertToAttachBody(
  userId: number,
  payload: UpsertCustomer
): AttachCustomerBody {
  const p: any = payload;
  return {
    userId,
    gender: p.gender,
    dob: p.dob,
    idType: p.idType,
    idNumber: p.idNumber,
    addressLine1: p.addressLine1,
    addressLine2: p.addressLine2,
    country: p.country,
    state: p.state,
    city: p.city,
    type: p.type,
    displayName: p.displayName,
    companyName: p.companyName,
    gstNumber: p.gstNumber,
    marketingOptIn: p.marketingOptIn,
    referralSource: p.referralSource,
    status: p.status,
    customerCode: p.customerCode,
    internalNotes: p.internalNotes,
  };
}

/** A pending "attach to existing user instead of create" decision. */
type AttachCandidate = {
  lookup: LookupUserResult;
  payload: UpsertCustomer;
  onSuccess?: () => void;
};

/** Best-effort mapping from edit form payload → API Update body */
function mapUiToUpdateBody(payload: UiCustomer): {
  id: number;
  data: UpdateCustomerBody;
} {
  const { id, ...rest } = payload as any;
  return {
    id: Number(id),
    data: rest as UpdateCustomerBody,
  };
}

/* ===========================================================================
 * Main Hook
 * =========================================================================== */

export function useCustomersPage() {
  /* ---------------- Filters, search, page ------------------------- */
  const [filters, setFilters] = useState<CustomerTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  // Reset to first page whenever filters or global search change
  useEffect(() => setPage(1), [filters, globalQ]);

  /* ---------------- Build server-side query ----------------------- */
  const listQuery: ListCustomersQuery = useMemo(() => {
    // Global search: prefer top-level search box, otherwise column filters
    const q =
      globalQ.trim() ||
      filters.firstName?.trim() ||
      filters.lastName?.trim() ||
      filters.email?.trim() ||
      filters.phone?.trim() ||
      undefined;

    const rawStatus = (filters.status ?? "") as CustomerStatusFilter;
    const status: ListCustomersQuery["status"] | undefined =
      rawStatus === "" ? undefined : rawStatus;


    const query: ListCustomersQuery = {
      page,
      limit: pageSize,
      q,
      status,
      // type / marketingOptIn can be wired later if needed
    };

    return query;
  }, [filters, globalQ, page, pageSize]);

  /* ---------------- React Query: list customers ------------------- */
  const { data, isLoading, isFetching } = useCustomers(listQuery);

  const apiItems: CustomerResult[] = data?.items ?? [];

  const allRows: UiCustomer[] = useMemo(
    () => apiItems.map(mapApiToUiCustomer),
    [apiItems]
  );

  const pageRows: CustomerRow[] = useMemo(
    () => allRows.map(mapUiToRow),
    [allRows]
  );

  const total = data?.total ?? 0;
  const effectivePage = data?.page ?? page;
  const effectivePageSize = data?.pageSize ?? pageSize;

  const totalPages = Math.max(
    1,
    Math.ceil(total / (effectivePageSize || pageSize))
  );
  const serialStart = (effectivePage - 1) * effectivePageSize;
  const start = serialStart;

  /* ---------------- Mutations (CRUD) ------------------------------ */
  const createMut = useCreateCustomerMutation();
  const attachMut = useAttachCustomerMutation();
  const updateMut = useUpdateCustomerMutation();
  const deleteMut = useDeleteCustomerMutation();

  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Pending "attach to existing user" prompt (email already belongs to a user).
  const [attachCandidate, setAttachCandidate] =
    useState<AttachCandidate | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  const submitCreate = (
    payload: UpsertCustomer,
    opts?: { onSuccess?: () => void }
  ) => {
    const body = mapUpsertToCreateBody(payload);
    createMut.mutate(body, {
      onSuccess: () => opts?.onSuccess?.(),
      onError: (err: any) => {
        setCreateError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to create customer."
        );
      },
    });
  };

  const onCreate = async (
    payload: UpsertCustomer,
    opts?: { onSuccess?: () => void }
  ) => {
    setCreateError(null);
    setAttachError(null);

    const email = String((payload as any).email ?? "").trim();

    // If the email already belongs to a user, offer to attach a customer
    // profile to that identity rather than failing on the duplicate-email rule.
    if (email) {
      setLookupBusy(true);
      try {
        const lookup = await lookupCustomerUser(email);
        if (lookup.exists) {
          if (lookup.isCustomer) {
            setCreateError(
              `${lookup.fullName ?? "This account"} is already a customer.`
            );
            return;
          }
          setAttachCandidate({ lookup, payload, onSuccess: opts?.onSuccess });
          return;
        }
      } catch {
        // Lookup failed — fall through to a normal create; the server still
        // enforces email uniqueness as a backstop.
      } finally {
        setLookupBusy(false);
      }
    }

    submitCreate(payload, opts);
  };

  const onConfirmAttach = () => {
    if (!attachCandidate?.lookup.userId) return;
    setAttachError(null);

    const body = mapUpsertToAttachBody(
      attachCandidate.lookup.userId,
      attachCandidate.payload
    );

    attachMut.mutate(body, {
      onSuccess: () => {
        const cb = attachCandidate.onSuccess;
        setAttachCandidate(null);
        cb?.();
      },
      onError: (err: any) => {
        setAttachError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to attach customer profile."
        );
      },
    });
  };

  const onCancelAttach = () => {
    setAttachCandidate(null);
    setAttachError(null);
  };

  const onEdit = (
    payload: UiCustomer,
    opts?: { onSuccess?: () => void }
  ) => {
    if (!payload?.id) return;

    setUpdateError(null);

    const { id, data } = mapUiToUpdateBody(payload);

    updateMut.mutate(
      { id, data },
      {
        onSuccess: () => {
          opts?.onSuccess?.();
        },
        onError: (err: any) => {
          setUpdateError(
            err?.response?.data?.message ??
              err?.message ??
              "Failed to update customer."
          );
        },
      }
    );
  };

  const onDelete = (
    id: number | string,
    opts?: { onSuccess?: () => void }
  ) => {
    deleteMut.mutate(id, {
      onSuccess: () => {
        opts?.onSuccess?.();
      },
    });
  };

  /* ---------------- Loading flag for UI --------------------------- */
  const customersLoading =
    isLoading || isFetching || createMut.isPending || updateMut.isPending;

  /* ---------------- Return API to ViewCustomers ------------------- */
  return {
    // Data for table
    allRows, // full UiCustomer objects
    pageRows, // current page (CustomerRow shape)
    serialStart,
    totalFiltered: total,
    totalPages,
    page: effectivePage,
    pageSize: effectivePageSize,
    start,

    // Filters & search
    filters,
    setFilters,
    globalQ,
    setGlobalQ,
    setPage,

    // Loading
    customersLoading,

    // CRUD actions
    onCreate,
    onEdit,
    onDelete,

    // Attach-to-existing-user flow
    attachCandidate,
    attachError,
    onConfirmAttach,
    onCancelAttach,
    attachSubmitting: attachMut.isPending,

    // Errors (for UI banners)
    createError,
    updateError,
    createSubmitting: createMut.isPending || lookupBusy,
    updateSubmitting: updateMut.isPending,
  };
}
